import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import API, { formatApiErrorDetail } from '../utils/axios';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Search, Ticket } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

export const CheckTicket = () => {
  const [searchParams] = useSearchParams();
  const [ticketCode, setTicketCode] = useState('');
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);

  const doSearch = async (code) => {
    setLoading(true);
    try {
      const { data } = await API.get(`/check-ticket/${encodeURIComponent(code)}`);
      setTicket(data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Tiket tidak ditemukan');
      setTicket(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const q = searchParams.get('code');
    if (q && q.trim()) {
      setTicketCode(q.trim());
      doSearch(q.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!ticketCode.trim()) {
      toast.error('Masukkan kode tiket');
      return;
    }
    doSearch(ticketCode.trim());
  };

  return (
    <div className="App">
      <Header />

      <section className="py-24 container mx-auto px-4 min-h-screen">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <Ticket className="inline-block text-[#FFD60A] mb-4" size={64} />
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight" data-testid="check-ticket-title">
              Cek Tiket
            </h1>
            <p className="text-gray-400 text-lg">Masukkan kode tiket untuk melihat detail prediksi Anda</p>
          </div>

          <div className="glass-card p-8 mb-8">
            <form onSubmit={handleSearch} className="space-y-6">
              <div>
                <label className="block text-white font-semibold mb-2">Kode Tiket</label>
                <input
                  type="text"
                  value={ticketCode}
                  onChange={(e) => setTicketCode(e.target.value)}
                  placeholder="Masukkan kode tiket Anda"
                  className="w-full"
                  data-testid="ticket-code-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="search-ticket-btn"
              >
                <Search size={20} />
                {loading ? 'Mencari...' : 'Cari Tiket'}
              </button>
            </form>
          </div>

          {ticket && (
            <div className="glass-card p-8" data-testid="ticket-details">
              <h2 className="text-2xl font-black text-white mb-6 text-center">Detail Tiket</h2>

              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Nomor Tiket</div>
                    <div className="text-white font-semibold">{ticket.ticket_number}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Status</div>
                    <span className={`status-badge ${
                      ticket.reward_status === 'winner' ? 'status-finished' :
                      ticket.reward_status === 'not_winner' ? 'status-closed' : 'status-upcoming'
                    }`}>
                      {ticket.reward_status === 'winner' ? 'Pemenang' :
                       ticket.reward_status === 'not_winner' ? 'Tidak Menang' : 'Pending'}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Website</div>
                    <div className="text-white font-semibold">{ticket.website}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm mb-1">User ID</div>
                    <div className="text-white font-semibold">{ticket.user_id}</div>
                  </div>
                </div>

                {ticket.match && (
                  <div>
                    <div className="text-gray-400 text-sm mb-2">Pertandingan</div>
                    <div className="glass-card p-4">
                      <div className="text-[#FFD60A] font-semibold text-sm mb-2">{ticket.match.league}</div>
                      <div className="text-white font-semibold">
                        {ticket.match.home_team} vs {ticket.match.away_team}
                      </div>
                      <div className="text-gray-400 text-sm mt-2">
                        {new Date(ticket.match.kickoff).toLocaleString('id-ID', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          timeZone: 'Asia/Jakarta',
                        })} WIB
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-gray-400 text-sm mb-1">Prediksi Anda</div>
                  <div className="text-3xl font-black text-[#FFD60A]">{ticket.prediction}</div>
                </div>

                <div>
                  <div className="text-gray-400 text-sm mb-1">Waktu Submit</div>
                  <div className="text-white">
                    {new Date(ticket.submitted_at).toLocaleString('id-ID', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      timeZone: 'Asia/Jakarta',
                    })} WIB
                  </div>
                </div>

                {ticket.qr_code && (
                  <div className="text-center">
                    <div className="text-gray-400 text-sm mb-4">QR Code</div>
                    <div className="qr-code-container mx-auto">
                      <QRCodeSVG value={ticket.ticket_code} size={200} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};